import { promises as fs } from "node:fs";
import { join, sep } from "node:path";
import { atomicSwapDir, ensureDir, pathExists, removeRecursive } from "./filesystem-helpers.js";
import { pruneNonRuntimeArtifacts } from "./supabase-pack-downloader.js";

// Path segments never copied into the pack from a local dev source.
const EXCLUDED_SEGMENTS = new Set([".git", "node_modules", ".claude"]);

function isExcluded(srcPath: string): boolean {
  return srcPath.split(sep).some((seg) => EXCLUDED_SEGMENTS.has(seg));
}

/**
 * Install a pack into `packDir` from a LOCAL source directory — no network, no
 * Supabase, no token. Mirrors downloadAndExtractPack's stage→prune→swap, but the
 * source is a directory copy instead of a downloaded tarball. Used by
 * `avatar sync --local <path>`.
 */
export async function installPackFromLocalDir(sourceDir: string, packDir: string): Promise<void> {
  if (!(await pathExists(sourceDir))) {
    throw new Error(`--local source không tồn tại: ${sourceDir}`);
  }
  const stat = await fs.stat(sourceDir);
  if (!stat.isDirectory()) {
    throw new Error(`--local source phải là thư mục: ${sourceDir}`);
  }
  // Guard: refuse to wipe the pack with a directory that has no pack content.
  const looksLikePack =
    (await pathExists(join(sourceDir, "skills"))) ||
    (await pathExists(join(sourceDir, "templates")));
  if (!looksLikePack) {
    throw new Error(
      `--local source không giống pack (thiếu skills/ hoặc templates/): ${sourceDir}`,
    );
  }

  const parent = join(packDir, "..");
  await ensureDir(parent);
  const tmpStageDir = `${packDir}.new-${process.pid}`;
  await removeRecursive(tmpStageDir);
  try {
    await fs.cp(sourceDir, tmpStageDir, {
      recursive: true,
      filter: (src) => !isExcluded(src),
    });
    await pruneNonRuntimeArtifacts(tmpStageDir);
    await atomicSwapDir(tmpStageDir, packDir);
  } finally {
    await removeRecursive(tmpStageDir).catch(() => {});
  }
}
